import {
  Card,
  Stack,
  Box,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Refresh, Error as ErrorIcon } from "@mui/icons-material";
import { formatTime } from "@pages/OrderTracker/components/PlanningAutomation/Timer";
import { useAutomationIntegrationDeploymentGetServerStatusQuery } from "@providers/hop-ord-server/api";
import { useState } from "react";

export const ServerStatusCard = () => {
  const [lastServerRefresh, setLastServerRefresh] = useState<Date>(new Date());

  const {
    data: serverStatus,
    isLoading: serverStatusLoading,
    error: serverStatusError,
    refetch: refetchServerStatus,
    isFetching: serverStatusFetching,
  } = useAutomationIntegrationDeploymentGetServerStatusQuery();

  const handleRefresh = () => {
    refetchServerStatus();
    setLastServerRefresh(new Date());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
      case "Connected":
        return "success";
      case "Disconnected":
        return "error";
      default:
        return "warning";
    }
  };

  return (
    <Card>
      <Stack padding={3} gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h2">
            Automation Server Status
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              Updated: {lastServerRefresh.toLocaleTimeString()}
            </Typography>
            <Tooltip title="Refresh server status">
              <IconButton
                onClick={handleRefresh}
                disabled={serverStatusFetching}
                color="primary"
                size="small"
              >
                {serverStatusFetching ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Refresh fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body1">Current Version:</Typography>
          {serverStatusLoading ? (
            <LinearProgress />
          ) : serverStatusError ? (
            <Chip label="Error loading" color="error" variant="outlined" />
          ) : (
            <Chip
              label={serverStatus?.version || "Unknown"}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        {serverStatus?.status && (
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body1">Server Status:</Typography>
            <Chip
              label={serverStatus.status}
              color={getStatusColor(serverStatus.status)}
              variant="outlined"
              size="small"
            />
          </Box>
        )}

        {serverStatus?.redisStatus && (
          <Stack gap={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body1">Redis Status:</Typography>
              <Chip
                label={
                  serverStatus.redisStatus.connected
                    ? "Connected"
                    : "Disconnected"
                }
                color={getStatusColor(
                  serverStatus.redisStatus.connected
                    ? "Connected"
                    : "Disconnected",
                )}
                variant="outlined"
                size="small"
              />
              {serverStatus.redisStatus.redisVersion && (
                <Typography variant="caption" color="text.secondary">
                  v{serverStatus.redisStatus.redisVersion}
                </Typography>
              )}
            </Box>

            {/* Redis Server Details */}
            <Box display="flex" gap={3} flexWrap="wrap" sx={{ ml: 2 }}>
              {serverStatus.redisStatus.uptimeInSeconds != null && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary">
                    Uptime:
                  </Typography>
                  <Typography variant="caption">
                    {formatTime(serverStatus.redisStatus.uptimeInSeconds)}
                  </Typography>
                </Box>
              )}

              {serverStatus.redisStatus.connectedClients != null && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary">
                    Clients:
                  </Typography>
                  <Typography variant="caption">
                    {serverStatus.redisStatus.connectedClients}
                  </Typography>
                </Box>
              )}

              {serverStatus.redisStatus.usedMemoryHuman != null && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary">
                    Memory:
                  </Typography>
                  <Typography variant="caption">
                    {serverStatus.redisStatus.usedMemoryHuman}
                  </Typography>
                </Box>
              )}

              {serverStatus.redisStatus.totalCommandsProcessed != null && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary">
                    Commands:
                  </Typography>
                  <Typography variant="caption">
                    {serverStatus.redisStatus.totalCommandsProcessed.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>

            {serverStatus.redisStatus.error && (
              <Box display="flex" alignItems="center" gap={1} sx={{ ml: 2 }}>
                <ErrorIcon color="error" fontSize="small" />
                <Typography variant="caption" color="error">
                  {serverStatus.redisStatus.error}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        <Divider />
      </Stack>
    </Card>
  );
};
